import { Injectable } from '@angular/core';
import { ClientCache, ClientEntity, ClientTrait } from './client.entity';
import { AuthService } from '../core-services/auth.service';
import { BaseEntityService } from '../core-services/base-entity.service';
import { genId, genVr } from '../../core/utils';
import { AccountService } from '../account/account.service';
import { ObservableService } from '../core-services/observable.service';
import { getFunctions, httpsCallable } from 'firebase/functions'
import { DatabaseService } from '../core-services/database.service';
import { AppointmentFullReducedTrait } from '../appointment/appointment.entity';
import { FeedTrait } from '../feed/feed.service';
import { InvoiceReducedBaseTrait } from '../invoice/invoice.entity';
import { AnalyticsService } from '../core-services/analytics.service';

export interface ClientProfileCacheTrait {
  next: AppointmentFullReducedTrait[];
  due: InvoiceReducedBaseTrait[];
  close: InvoiceReducedBaseTrait[];
  outdated?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ClientService extends BaseEntityService<ClientEntity, ClientTrait> {

  public clients: ClientEntity[] = [];

  public get hasReachedPlanLimit() {
    return false
  }

  public _selectedClient: ClientTrait;

  public get selectedClient() {
    return this._selectedClient;
  }


  constructor(
   private auth: AuthService,
   private account: AccountService,
   private observable: ObservableService,
   private database: DatabaseService,
   private analytics: AnalyticsService,
  ) {
    super();
   }

  public async init() {
    this.loading++;
    if (!this.initialized) {
      await this.refresh();
    }
    this.initialized = true;
    this.loading--;
  }



  public async refresh() {
    this.clients = [];
    return await this.load();
  }

  public async create(data: Omit<ClientTrait, 'id' | 'createdAt'>) {
    this.loading++;
    try {
    const email = await ClientEntity.findIndex<ClientEntity>('email', data.email);
    if (email) {
      throw new Error("emailAlreadyExists");
    }
    const client = this.normalize(data);
    await client.persist();

    setTimeout(async () => await this.auth.refreshToken(), 2000);


    return client;
    } catch(e) {

      const permissionError = 'Missing or insufficient permissions.' == e.message;
      if (permissionError) setTimeout(() => this.observable.publishOnSubscriptionNeeded(), 1000);

      throw e;
    } finally {
      this.loading--;
    }
  }


  public async edit(data: ClientTrait) {
    const entity = ClientEntity.from<ClientEntity>({...data});
    entity.uName = this.account.account.name;
    entity.version = genVr();
    let pos = -1;
    for (let i = 0; i < this.clients.length; i++) {
      if (this.clients[i].id === entity.id) {
        pos = i;
        break;
      }
    }

    if (this.clients[pos].email != entity.email) {
      const email = await ClientEntity.findIndex<ClientEntity>('email', entity.email);
      if (email) {
        throw new Error("Email already exists");
      } else if(!(await ClientEntity.deleteIndex<ClientEntity>('email', this.clients[pos].email))) {
        throw new Error("Error updating email");
      }
    }
    
    await entity.persist();
    this.clients[pos] = entity;
    this.observable.publishClientEdit(entity.id);
    return entity;    
  }

  public async delete(id) {
    const result = await ClientEntity.delete(id);
    if (result) {
      const index = this.clients.findIndex((item) => item.id === id);
      if (index > -1) {
        this.clients.splice(index, 1);
      }
    }
    this.observable.publishClientDelete(id);
    return result;
  }

  public async find(id: string, useCache: boolean = true): Promise<ClientEntity> {
    this.loading++;
    try {
      let clientCache;
   
     if (useCache) {
      // clientCache = DatabaseService.instance.getCache<ClientEntity>(`${ClientCache.ref(id)}/d}`);
      clientCache = this.clients.find(c => c.id == id);
    
      if (clientCache) {
        clientCache = ClientEntity.from<ClientEntity>(clientCache);
      }
     }


     return clientCache || await ClientEntity.load<ClientEntity>(id);
    } finally {
      this.loading--;
    }
  }

  public async findByEmail(email: string) {
    this.loading++;
    try {
    const client = await ClientEntity.loadWhere<ClientEntity>('email', email);
    return client;
    } finally {
      this.loading--;
    }
  }

  public async load(offset?: string) {
    this.loading++;
    try {
      const clients = await ClientEntity.loadAll<ClientEntity>(AuthService.instance.currentUser.uid, {
        offset,
        orderBy: {
          column: 'idxName',
          sort: 'asc'
        },
      });
      return this.clients.push(...clients);
    } finally {
      this.loading--;
    }
  }

  public openCreateModal(item?: ClientTrait) {
    this.editingEntity = !!item ? {...item} : null;
    this.isModalOpen = true;
    this.analytics.openClientModal(!!item ? 'update' : 'create');
  }

  public closeCreateModal() {
    this.editingEntity = null;
    this.isModalOpen = false;
  }

  public normalize(data: Omit<ClientTrait, 'id' | 'createdAt'>) {

    const client = new ClientEntity({
      ...data,
      id: genId(),
      phone: data.phone?.replace(/\D/g,''),
      price: data.price || this.account.account?.settings?.appointmentPrice || 0,
    });

    client.uName = this.account.account.name;
    return client;
  }

  public viewClientProfile(client: ClientTrait) {
    this._selectedClient = client;
  }

  public async findAllByName(name: string, limit: number = 10, offset?: string) {
    this.loading++;
    try {
      return await ClientEntity.loadAllFromDatabase<ClientEntity>(
        AuthService.instance.currentUser.uid, 
        {
          parentName: 'users',
          orderBy: {
            column: 'idxName',
            sort: 'asc'
          },
          limit,
          offset,
          whereIncludes: {
            column: 'idxNameSearch',
            value: name.toLowerCase()
          }
        }
      );
    } finally {
      this.loading--;
    }
  }

  public async getProfile(id: string): Promise<ClientProfileCacheTrait> {
    this.startLoading();
    try {
    const data = await this.database.getCache<{
      aps: { dt: AppointmentFullReducedTrait[]; ua: number; ea: number; ot?: boolean; };
      pps: { dt: InvoiceReducedBaseTrait[]; ua: number; ea: number; ot?: boolean; };
      cdps: { dt: InvoiceReducedBaseTrait[]; ua: number; ea: number; ot?: boolean; };
    }>(ClientCache.feedRef(id));

    const agendaUpdatedAt = await this.database
    .getCache<number>(`${ClientCache.agendaRef(id)}/ua`);

    const now = Date.now();
    console.log(`${ClientCache.agendaRef(id)}/ua`, now)

    return {
      next: data?.aps?.dt || [],
      due: data?.pps?.dt || [],
      close: data?.cdps?.dt || [],
      outdated: ((data?.aps?.ua || 0) < agendaUpdatedAt && now > (data?.aps?.ea || 0) || data?.aps?.ot || data?.pps?.ot || data?.cdps?.ot)
    }
    
    } finally {
      this.stopLoading();
    }
  }

  async getProfileFromEndpoint(id: string) {
    return await this.getFromEndpoint<FeedTrait>(`getViewClientFeed?cId=${id}`);
  }

  private async getFromEndpoint<T>(name: string) {

    const functions = getFunctions();
    const request = httpsCallable(functions, name);
    const data: T = (await request()).data as T;

    return data;
  }
}
